import React from "react";
import { ExerciseEditorProps } from "./exercise-editor-types";
import { SelectForBlankExerciseContent } from "@org/ui";
import { Button } from "@org/ui";
import { Label } from "@org/ui";
import { TextArea } from "@org/ui";
import { Input } from "@org/ui";
import { RadioGroup, RadioGroupItem } from "@org/ui";
import { XIcon } from "lucide-react";

const createSentenceId = () => `sfb_sentence_${Math.random().toString(36).slice(2, 10)}`;
const createBlankId = () => `sfb_blank_${Math.random().toString(36).slice(2, 10)}`;
const createOptionId = () => `sfb_option_${Math.random().toString(36).slice(2, 10)}`;

const createBlank = (blankIndex: number) => ({
  blankId: createBlankId(),
  position: blankIndex,
  options: [
    { optionId: createOptionId(), label: "", isCorrect: true, order: 0 },
    { optionId: createOptionId(), label: "", isCorrect: false, order: 1 }
  ]
});

const createSentence = (order: number) => ({
  sentenceId: createSentenceId(),
  sentence: "",
  order,
  correction: "",
  blanks: [createBlank(0)]
});

export const SelectForBlankExerciseEditor: React.FC<ExerciseEditorProps<SelectForBlankExerciseContent>> = ({
  content,
  updateContent
}) => {
  const sentences = content.data.sentences ?? [];

  const applySentences = (nextSentences: SelectForBlankExerciseContent["data"]["sentences"]) => {
    const normalised = nextSentences.map((sentence, sentenceIndex) => ({
      ...sentence,
      sentenceId: sentence.sentenceId ?? createSentenceId(),
      order: sentenceIndex,
      blanks: (sentence.blanks ?? []).map((blank, blankIndex) => ({
        ...blank,
        blankId: blank.blankId ?? createBlankId(),
        position: blankIndex,
        options: (blank.options ?? []).map((option, optionIndex) => ({
          ...option,
          optionId: option.optionId ?? createOptionId(),
          order: optionIndex,
          isCorrect: option.isCorrect ?? optionIndex === 0
        }))
      }))
    }));

    updateContent({
      ...content,
      data: {
        ...content.data,
        sentences: normalised
      }
    });
  };

  const handleSentenceChange = (index: number, value: string) => {
    applySentences(
      sentences.map((sentence, idx) =>
        idx === index
          ? { ...sentence, sentence: value }
          : sentence
      )
    );
  };

  const handleAddSentence = () => {
    applySentences([
      ...sentences,
      createSentence(sentences.length)
    ]);
  };

  const handleRemoveSentence = (index: number) => {
    if (sentences.length <= 1) return;
    applySentences(sentences.filter((_, idx) => idx !== index));
  };

  const handleSentenceCorrectionChange = (sentenceIndex: number, correction: string) => {
    applySentences(
      sentences.map((sentence, idx) =>
        idx === sentenceIndex ? { ...sentence, correction } : sentence
      )
    );
  };

  const handleOptionChange = (sentenceIndex: number, blankIndex: number, optionIndex: number, label: string) => {
    applySentences(
      sentences.map((sentence, idx) => {
        if (idx !== sentenceIndex) return sentence;
        const blanks = sentence.blanks ?? [];
        return {
          ...sentence,
          blanks: blanks.map((blank, innerIdx) => {
            if (innerIdx !== blankIndex) return blank;
            const options = blank.options ?? [];
            return {
              ...blank,
              options: options.map((option, optIdx) =>
                optIdx === optionIndex ? { ...option, label } : option
              )
            };
          })
        };
      })
    );
  };

  const handleMarkCorrect = (sentenceIndex: number, blankIndex: number, optionIndex: number) => {
    applySentences(
      sentences.map((sentence, idx) => {
        if (idx !== sentenceIndex) return sentence;
        const blanks = sentence.blanks ?? [];
        return {
          ...sentence,
          blanks: blanks.map((blank, innerIdx) => {
            if (innerIdx !== blankIndex) return blank;
            const options = blank.options ?? [];
            return {
              ...blank,
              options: options.map((option, optIdx) => ({
                ...option,
                isCorrect: optIdx === optionIndex
              }))
            };
          })
        };
      })
    );
  };

  const handleRemoveBlank = (sentenceIndex: number, blankIndex: number) => {
    applySentences(
      sentences.map((sentence, idx) => {
        if (idx !== sentenceIndex) return sentence;
        const blanks = sentence.blanks ?? [];
        if (blanks.length <= 1) return sentence;
        const remaining = blanks.filter((_, innerIdx) => innerIdx !== blankIndex);
        return {
          ...sentence,
          blanks: remaining
        };
      })
    );
  };

  const handleAddBlank = (sentenceIndex: number) => {
    applySentences(
      sentences.map((sentence, idx) => {
        if (idx !== sentenceIndex) return sentence;
        const blanks = sentence.blanks ?? [];
        return {
          ...sentence,
          blanks: [...blanks, createBlank(blanks.length)]
        };
      })
    );
  };

  const handleAddOption = (sentenceIndex: number, blankIndex: number) => {
    applySentences(
      sentences.map((sentence, idx) => {
        if (idx !== sentenceIndex) return sentence;
        const blanks = sentence.blanks ?? [];
        return {
          ...sentence,
          blanks: blanks.map((blank, innerIdx) => {
            if (innerIdx !== blankIndex) return blank;
            const options = blank.options ?? [];
            return {
              ...blank,
              options: [...options, { optionId: createOptionId(), label: "", isCorrect: false, order: options.length }]
            };
          })
        };
      })
    );
  };

  const handleRemoveOption = (sentenceIndex: number, blankIndex: number, optionIndex: number) => {
    applySentences(
      sentences.map((sentence, idx) => {
        if (idx !== sentenceIndex) return sentence;
        const blanks = sentence.blanks ?? [];
        return {
          ...sentence,
          blanks: blanks.map((blank, innerIdx) => {
            if (innerIdx !== blankIndex) return blank;
            const options = blank.options ?? [];
            if (options.length <= 2) return blank;
            const remaining = options.filter((_, optIdx) => optIdx !== optionIndex);
            const hasCorrect = remaining.some(option => option.isCorrect);
            if (!hasCorrect && remaining.length > 0) {
              remaining[0] = { ...remaining[0], isCorrect: true };
            }
            return {
              ...blank,
              options: remaining
            };
          })
        };
      })
    );
  };

  return (
    <div className="space-y-4">
      {sentences.map((sentence, sentenceIndex) => (
        <div key={sentence.sentenceId ?? sentenceIndex} className="rounded-lg border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <Label>Sentence {sentenceIndex + 1}</Label>
            {sentences.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-red-500"
                onClick={() => handleRemoveSentence(sentenceIndex)}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>

          <TextArea
            value={sentence.sentence}
            onChange={(event) => handleSentenceChange(sentenceIndex, event.target.value)}
            placeholder="Sentence with blanks (use ___ as placeholders)"
          />

          <div>
            <Label htmlFor={`correction-${sentenceIndex}`}>Correction Guidance (for this sentence)</Label>
            <TextArea
              id={`correction-${sentenceIndex}`}
              value={sentence.correction ?? ""}
              onChange={(event) => handleSentenceCorrectionChange(sentenceIndex, event.target.value)}
              placeholder="How should this sentence be evaluated?"
              className="mt-1"
            />
          </div>

          <div className="space-y-3">
            {(sentence.blanks ?? []).map((blank, blankIndex) => {
              const options = blank.options ?? [];
              const activeIndex = Math.max(0, options.findIndex(option => option.isCorrect));

              return (
                <div key={blank.blankId ?? blankIndex} className="rounded-md border border-dashed p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Blank {blankIndex + 1}</Label>
                    {(sentence.blanks?.length ?? 0) > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-red-500"
                        onClick={() => handleRemoveBlank(sentenceIndex, blankIndex)}
                      >
                        <XIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Options</Label>
                    <RadioGroup
                      value={activeIndex >= 0 ? activeIndex.toString() : undefined}
                      onValueChange={(value) => handleMarkCorrect(sentenceIndex, blankIndex, parseInt(value, 10))}
                      className="space-y-2"
                    >
                      {options.map((option, optionIndex) => {
                        const isSelected = option.isCorrect;
                        const optionId = option.optionId ?? `option-${sentenceIndex}-${blankIndex}-${optionIndex}`;

                        return (
                          <label
                            key={optionId}
                            htmlFor={`${optionId}-radio`}
                            className={`group flex items-center gap-3 rounded-lg border p-3 transition-all cursor-pointer ${
                              isSelected
                                ? "border-emerald-500 bg-emerald-50 shadow-sm ring-2 ring-emerald-200"
                                : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30"
                            }`}
                          >
                            <RadioGroupItem
                              id={`${optionId}-radio`}
                              value={optionIndex.toString()}
                              className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <Input
                              value={option.label}
                              placeholder={`Option ${optionIndex + 1}`}
                              onChange={(event) => handleOptionChange(sentenceIndex, blankIndex, optionIndex, event.target.value)}
                              className="flex-1"
                            />
                            {options.length > 2 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500 hover:text-red-500"
                                onClick={() => handleRemoveOption(sentenceIndex, blankIndex, optionIndex)}
                              >
                                <XIcon className="h-4 w-4" />
                              </Button>
                            )}
                          </label>
                        );
                      })}
                    </RadioGroup>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddOption(sentenceIndex, blankIndex)}
                    >
                      Add Option
                    </Button>
                  </div>
                </div>
              );
            })}

            <Button variant="outline" size="sm" onClick={() => handleAddBlank(sentenceIndex)}>
              Add Blank
            </Button>
          </div>
        </div>
      ))}

      <Button variant="outline" size="sm" onClick={handleAddSentence}>
        Add Sentence
      </Button>
    </div>
  );
};
